import {
    calculateSizeInputs,
    getDefaultSize,
    getSizeParams,
    type SizeDeps,
} from "@/modules/compute/commonViewModel.ts";
import type { SelectedOs } from "@/modules/compute/os/os.ts";
import { getDefaultDesignatedPoolForLocation } from "@/modules/compute/pool/pool.ts";
import { usePackages } from "@/modules/compute/vmPackages.ts";
import type { VmSizeSelectLoaderProps } from "@/modules/compute/VmSizeSelect.tsx";
import type { SizeValue, VmSizeParams } from "@/modules/compute/vmSizeSelectUtils.ts";
import { getPreferredLocation } from "@/modules/location/location.ts";
import { useLocationsForType } from "@/modules/location/locationQuery.ts";
import { canCreateResourceHostPoolsAtom } from "@/modules/pools/hostPoolsQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useOnce } from "@/utils/react/useOnce.ts";
import { proxy, useSnapshot } from "valtio";

/* eslint-disable react-hooks/immutability -- React compiler / ESLint bug - does not understand `useOnce` / Valtio usage */

export function useSizeSelect(existing: SizeValue | undefined, initialOs: SelectedOs, sizeParams?: VmSizeParams) {
    const packages = usePackages();
    const locations = useLocationsForType("virtual_machine");
    const pools = useSuspenseQueryAtom(canCreateResourceHostPoolsAtom);

    const defaultLocation = getPreferredLocation(locations.locations);
    const defaultPool = getDefaultDesignatedPoolForLocation(pools, defaultLocation?.slug);
    const params = sizeParams ?? getSizeParams(defaultPool?.guest_limits);

    const store = useOnce(() => {
        const deps = {
            // NB: These are not reactively updated, but that is an edge case (and would require more overcomplicated clamping logic)
            _packages: packages,
            _sizeParams: params,
            _os: initialOs,
            _diskSize: existing?.disks,
        } satisfies SizeDeps;

        return proxy({
            sizes: calculateSizeInputs.call(deps, existing),
            deps,
        });
    });

    function setOs(value: SelectedOs) {
        console.debug("Setting OS", value);

        store.deps._os = value;
        // Reset size after changing OS (in case previous size is not valid for the new OS)
        setSize(getDefaultSize.call(store.deps));
    }

    function setSize(value: SizeValue) {
        store.sizes = calculateSizeInputs.call(store.deps, value);
    }

    const {
        sizes: { size, size_ranges },
        deps: { _os },
    } = useSnapshot(store) as typeof store;

    return {
        size,
        _os,
        setOs,
        props: {
            value: size,
            // TODO: Constraints are duplicated inside the common view model
            constraints: {
                currentDiskSize: existing?.disks,
                // XXX: Since this is only used for package filtering, we should not use the CPU/RAM ratio constraint
                // cpuCount: size.vcpu,
            },
            sliderRanges: size_ranges,
            onChange: setSize,
        } satisfies Partial<VmSizeSelectLoaderProps>,
    };
}
